import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  CampaignNPC,
  CampaignObject,
  Encounter,
  VendorService, 
} from "@dndModels";

import DandDAPI from "@dndUtilities/system_api";
import { makeStyles } from "@utilities/theme_utilities";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import CommonUtilities from "@utilities/common_utilities";

import FlexBox from "@components/layout/FlexBox";
import VendorServiceInput from "./VendorServiceInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";

interface Props {
  campaign: Campaign;
  npc: CampaignNPC;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function VendorServicesInput({
  campaign,
  npc
}: Props) {
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<VendorService | undefined>(undefined);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderServices = () => {
    return (
      <FlexBox direction="column" style={{ width: "100%" }}>
        <FlexBox className="header centered">Services</FlexBox>
        <FlexBox className="padded">
          <AddButton label="Add Service" 
            disabled={processing}
            onClick={ async () => {
              const new_service = new VendorService();
              setProcessing(true);
              npc.services.push(new_service);
              await api.upsertObject("campaign_npc", npc);
              setSelected(new_service);
              setProcessing(false);
            }}
          />
        </FlexBox>
        <FlexBox>
          <Grid container spacing={0} direction="row">
            { npc.services.map(vendor_service => {
              return (
                <Grid size={4} key={vendor_service.true_id}>
                  <div className="padded">
                    <FlexBox direction="row" className={cx(classes.root)}>
                      <FlexBox className="header centered clickable" onClick={() => setSelected(selected && selected.true_id === vendor_service.true_id ? undefined : vendor_service) }>{ vendor_service.name }</FlexBox>
                      <FlexBox xs_expand={0}>
                        <div style={{ float: "right"}}>
                          <DeleteButton
                            no_fab
                            obj_name={vendor_service.name}
                            onClick={ async () => {
                              if (selected && selected.true_id === vendor_service.true_id) {
                                setSelected(undefined);
                              }
                              npc.services = npc.services.filter(o => o.true_id !== vendor_service.true_id);
                              await api.upsertObject("campaign_npc", npc);
                              setPing(!ping);
                            }} 
                          />
                        </div>
                      </FlexBox>
                    </FlexBox>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </FlexBox>
        { renderSelected() }
      </FlexBox>
    );
  }

  const renderSelected = () => {
    if (selected) {
      return (
        <FlexBox>
          <VendorServiceInput
            campaign={campaign}
            npc={npc} 
            vendor_service={selected}
          />
        </FlexBox>
      );
    }
    return null;
  }

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={open}
          width={"80%"}
          onClose={() => { setOpen(false); }}>
          { renderServices() }
        </ModalOrDrawer>
      </>
    )
  }

  return (
    <FlexBox>
      <div className="padded">
        <FlexBox className={cx(classes.root, "header centered clickable")} onClick={() => { setOpen(true); }}>
          Services
        </FlexBox>
        { renderExtras() }
      </div>
    </FlexBox>
  );
}
